import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { 
  FileText, 
  Download, 
  Share2, 
  Search,
  Clock,
  User,
  Shield,
  Lock,
  Eye,
  AlertTriangle,
  CheckCircle,
  Building,
  Calendar,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Printer
} from 'lucide-react';

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentTitle: string;
}

interface DocumentData {
  title: string;
  version: string;
  lastUpdated: string;
  author: string;
  department: string;
  classification: 'Public' | 'Internal' | 'Confidential' | 'Restricted';
  accessLevel: 'All Employees' | 'Managers Only' | 'HR Only' | 'Executive Team';
  documentType: 'Policy' | 'Procedure' | 'Guide' | 'Manual' | 'Form';
  approvalStatus: 'Draft' | 'Under Review' | 'Approved' | 'Archived';
  nextReview: string;
  content: string;
}

export function DocumentViewer({ isOpen, onClose, documentTitle }: DocumentViewerProps) {
  const [zoomLevel, setZoomLevel] = useState(100);

  // Real-life policy documents with proper PDF-like structure
  const getDocumentContent = (title: string): DocumentData => {
    switch (title.toLowerCase()) {
      case 'employee handbook section 4.2':
        return {
          title: 'Employee Handbook - Section 4.2: Time Off and Leave Policies',
          version: '3.1',
          lastUpdated: '2024-01-15',
          author: 'Jennifer Martinez',
          department: 'Human Resources',
          classification: 'Internal',
          accessLevel: 'All Employees',
          documentType: 'Policy',
          approvalStatus: 'Approved',
          nextReview: '2025-01-15',
          content: `AKME CORPORATION
EMPLOYEE HANDBOOK

Section 4.2: Time Off and Leave Policies

Document ID: HR-POL-4.2-2024
Version: 3.1
Effective Date: January 15, 2024
Last Revision: January 15, 2024
Next Review: January 15, 2025

POLICY STATEMENT

Akme Corporation recognizes the importance of work-life balance and provides comprehensive time off benefits to support employee well-being, family needs, and personal obligations. This policy establishes guidelines for requesting, approving, and tracking all forms of employee leave.

SCOPE AND APPLICABILITY

This policy applies to all regular full-time and part-time employees of Akme Corporation and its subsidiaries. Temporary, contract, and consultant workers are not eligible for the benefits outlined in this policy unless specifically stated in their individual agreements.

PAID TIME OFF (PTO)

Eligibility Requirements
• Full-time employees (40 hours/week): Eligible after 90 days of continuous employment
• Part-time employees (20+ hours/week): Eligible on pro-rated basis after 90 days
• Temporary and contract employees: Not eligible for PTO benefits
• Rehired employees: Previous service may be considered for accrual rate determination

Accrual Schedule
PTO accrues monthly on the last day of each month based on length of continuous service:

Service Period          Annual Entitlement     Monthly Accrual
Years 1-2               15 days (120 hours)    10.00 hours
Years 3-5               20 days (160 hours)    13.33 hours  
Years 6-10              25 days (200 hours)    16.67 hours
Years 11+               30 days (240 hours)    20.00 hours

Usage Guidelines and Procedures
• Minimum request increment: 4 hours (half-day)
• Advance notice requirements:
  - 1-3 days: 14 calendar days notice
  - 4+ consecutive days: 30 calendar days notice
  - Peak season requests: 60 calendar days notice
• All requests subject to supervisor approval and business needs
• Coverage arrangements must be documented and approved

Request and Approval Process
1. Employee submits request through online portal
2. Direct supervisor reviews within 3 business days
3. HR reviews requests requiring policy interpretation
4. Employee receives written confirmation or denial
5. Approved time automatically updates payroll and schedules

Carryover and Forfeiture Policy
• Maximum carryover: 40 hours (5 days) to following calendar year
• Forfeiture date: December 31st of each year
• No cash payout for unused PTO upon termination
• Medical exception: Extended carryover may be approved with documentation

SICK LEAVE POLICY

Unlimited Sick Leave Benefits
Akme Corporation provides unlimited sick leave for all eligible employees to ensure adequate time for health recovery without financial hardship.

Eligible Uses of Sick Leave
• Personal illness, injury, or medical condition
• Medical, dental, or vision appointments
• Care for immediate family members (spouse, children, parents)
• Mental health days (up to 5 per year without documentation)
• Preventive medical care and procedures
• Recovery time following medical procedures

Documentation Requirements
Days 1-3: No medical documentation required
Days 4+: Medical certification from healthcare provider
Recurring patterns: HR may request additional documentation
FMLA qualifying events: Federal documentation requirements apply

Reporting Procedures
• Notify supervisor before scheduled shift start time
• Submit sick leave request in portal within 24 hours
• Provide estimated return date if known
• Update supervisor on status for extended absences

PERSONAL DAYS

Annual Allocation
• Full-time employees: 3 personal days per calendar year
• Part-time employees: Pro-rated based on scheduled hours
• New hires: Pro-rated based on start date
• Must be used within calendar year (no carryover permitted)

Intended Uses
• Religious observances and cultural celebrations
• Personal business requiring work day scheduling
• Family obligations and commitments
• Educational activities and community service
• Personal appointments that cannot be rescheduled

COMPANY HOLIDAYS

Observed Holidays (10 Annual Days)
1. New Year's Day - January 1
2. Martin Luther King Jr. Day - Third Monday in January
3. Presidents' Day - Third Monday in February
4. Memorial Day - Last Monday in May
5. Independence Day - July 4
6. Labor Day - First Monday in September
7. Indigenous Peoples' Day - Second Monday in October
8. Thanksgiving Day - Fourth Thursday in November
9. Day After Thanksgiving - Fourth Friday in November
10. Christmas Day - December 25

Floating Holidays (2 Annual Days)
• Must be scheduled with advance supervisor approval
• Can be used for religious observances or personal preference
• Must be used within calendar year
• Cannot be carried over or cashed out

Holiday Pay and Scheduling
• Regular scheduled hours paid at normal rate
• Holiday work: Time-and-a-half plus holiday pay
• Part-time employees: Holiday pay based on average weekly hours
• Holiday falling on weekend: Observed on adjacent weekday

FAMILY AND MEDICAL LEAVE

Federal FMLA Entitlements
Eligible employees may take up to 12 weeks of unpaid, job-protected leave for:
• Birth or adoption of a child
• Serious health condition of employee
• Care for family member with serious health condition
• Military family obligations and emergencies

Eligibility Requirements
• 12 months of employment with company
• 1,250 hours worked in previous 12 months
• Work location with 50+ employees within 75 miles

State Leave Law Compliance
Additional leave entitlements may be available under applicable state laws. Employees should consult with HR to understand all available benefits and requirements.

BEREAVEMENT LEAVE

Immediate Family Leave (5 Paid Days)
• Spouse or domestic partner
• Children, stepchildren, foster children
• Parents, stepparents, parents-in-law
• Siblings and step-siblings
• Grandparents and grandchildren

Extended Family Leave (3 Paid Days)
• Aunts, uncles, nieces, nephews
• Other relatives residing in employee household
• Close personal friends (at manager discretion)

Additional Considerations
• Additional unpaid leave may be granted
• Travel time for distant services considered case-by-case
• Funeral attendance for colleagues: Up to 4 hours paid time
• Grief counseling resources available through EAP

MILITARY LEAVE

Types of Military Service Leave
• Active duty deployment
• National Guard and Reserve training
• Emergency activation and mobilization
• Military family support activities

Rights and Protections
• Unpaid leave as required by USERRA
• Health insurance continuation options
• Pension and benefit continuation during qualified leave
• Seniority and advancement rights protection
• Guaranteed job restoration upon return

JURY DUTY AND COURT APPEARANCES

Jury Service Compensation
• Full salary continuation during jury duty service
• No deduction from PTO or other leave balances
• Official court documentation required
• Return to work if dismissed early in day

Court Appearance Leave
• Unpaid leave for personal legal matters
• Paid leave for company-related legal proceedings
• Subpoena situations evaluated individually
• Advance notice required when possible

COMPLIANCE AND LEGAL REQUIREMENTS

Federal Law Compliance
This policy ensures compliance with:
• Family and Medical Leave Act (FMLA)
• Americans with Disabilities Act (ADA)
• Uniformed Services Employment and Reemployment Rights Act (USERRA)
• Fair Labor Standards Act (FLSA)

State and Local Law Variations
Some jurisdictions may provide additional leave rights beyond this policy. Employees should consult with HR regarding location-specific benefits and requirements.

ADMINISTRATION AND CONTACT INFORMATION

Policy Questions and Support
Human Resources Department
Email: hr-timeoff@akme.com
Phone: (555) 123-4567 ext. 2100
Employee Portal: portal.akme.com/timeoff

Emergency Contacts
After-hours urgent matters: (555) 123-4567 ext. 9999
HR Director: jennifer.martinez@akme.com
Payroll Questions: payroll@akme.com

Manager Resources
Management toolkit and training materials available at:
portal.akme.com/managers/timeoff

DOCUMENT CONTROL

Document Information
Policy Owner: Director of Human Resources
Approval Authority: Chief Human Resources Officer
Document Classification: Internal Use Only
Distribution: All Employees via Employee Portal

Version History
Version 3.1 - January 15, 2024 - Updated FMLA regulations
Version 3.0 - January 1, 2024 - Annual policy review
Version 2.9 - June 15, 2023 - State law compliance updates

This document supersedes all previous versions of the Time Off and Leave Policy.`
        };
        
      case 'pto policy 2024':
        return {
          title: 'Paid Time Off (PTO) Policy and Procedures Guide',
          version: '2.3',
          lastUpdated: '2024-01-01',
          author: 'Human Resources Department',
          department: 'Human Resources',
          classification: 'Internal',
          accessLevel: 'All Employees',
          documentType: 'Procedure',
          approvalStatus: 'Approved',
          nextReview: '2024-12-31',
          content: `AKME CORPORATION
PTO POLICY AND PROCEDURES GUIDE 2024

Document ID: HR-PROC-PTO-2024
Version: 2.3
Effective Date: January 1, 2024
Distribution: All Employees

TABLE OF CONTENTS

1. Introduction and Overview
2. PTO Accrual Rates and Eligibility
3. Request Submission Procedures
4. Approval Process and Timeline
5. Best Practices for PTO Planning
6. Year-End Management
7. Integration with Other Leave Types
8. Technology Resources and Support
9. Frequently Asked Questions
10. Contact Information

SECTION 1: INTRODUCTION AND OVERVIEW

Purpose of This Guide
This comprehensive guide provides step-by-step procedures for effectively managing your Paid Time Off (PTO) benefits at Akme Corporation. It supplements the official PTO policy and offers practical guidance for employees and managers.

2024 Updates and Changes
• Enhanced online portal functionality
• Updated approval workflow processes
• New mobile application features
• Revised carryover notification timeline
• Expanded manager reporting capabilities

Key Principles
• Plan ahead for optimal approval chances
• Consider business needs and team coverage
• Use PTO regularly to maintain work-life balance
• Communicate clearly with supervisors and colleagues
• Understand integration with other leave types

SECTION 2: PTO ACCRUAL RATES AND ELIGIBILITY

Current Accrual Schedule (2024)
The following accrual rates are effective January 1, 2024:

Length of Service     Annual Days     Monthly Accrual     Weekly Accrual
0-2 years            15 days         1.25 days           0.29 days
3-5 years            20 days         1.67 days           0.38 days
6-10 years           25 days         2.08 days           0.48 days
11+ years            30 days         2.50 days           0.58 days

Eligibility Verification
• Full-time status: 40 hours per week
• Part-time status: 20+ hours per week (pro-rated benefits)
• 90-day waiting period for new employees
• Rehires: Previous service credit may apply

Balance Tracking
• Real-time balance updates in employee portal
• Monthly accrual processed on last business day
• Balance caps at 1.5x annual entitlement
• Forfeiture warnings sent quarterly

SECTION 3: REQUEST SUBMISSION PROCEDURES

Step-by-Step Request Process

Step 1: Access the Employee Portal
1. Navigate to portal.akme.com
2. Log in with company credentials
3. Select "Time Off & Benefits" from main menu
4. Click "Request Time Off"

Step 2: Check Your Current Balance
• Review available PTO balance
• Check pending requests
• Note upcoming accruals
• Verify blackout period restrictions

Step 3: Select Request Details
• Choose request type (PTO, Sick, Personal)
• Select start and end dates
• Specify full day or partial day
• Enter business justification if required

Step 4: Coverage Planning
• Identify coverage for essential responsibilities
• Document handoff procedures
• Set up email auto-reply message
• Brief team on urgent decision authority

Step 5: Submit for Approval
• Review all details for accuracy
• Submit to direct supervisor
• Track approval status in portal
• Respond promptly to any questions

Advance Notice Requirements

Request Duration          Minimum Notice
1-2 days                 14 calendar days
3-5 days                 21 calendar days
6+ consecutive days      30 calendar days
Peak season periods     60 calendar days
Holiday periods         Per department schedule

Emergency Situations
When advance notice is not possible:
• Contact supervisor immediately by phone
• Submit formal request within 24 hours
• Provide relevant documentation
• Follow up on coverage arrangements

SECTION 4: APPROVAL PROCESS AND TIMELINE

Manager Approval Workflow
Managers have 3 business days to respond to PTO requests:

Day 1: Initial Review
• Request notification sent to manager
• Preliminary business impact assessment
• Coverage availability verification

Day 2-3: Decision and Communication
• Final approval or denial determination
• Communication to employee via portal
• Alternative suggestions if denied
• Coverage confirmation if approved

Approval Factors Considered
• Critical business periods and deadlines
• Team coverage capabilities and availability
• Employee performance and attendance record
• Previous PTO usage patterns and fairness
• Customer service and operational impact

Denial Reasons and Alternatives
Common denial reasons:
• Insufficient coverage available
• Critical business period or deadline
• Multiple team members already scheduled
• Recent performance or attendance issues

When requests are denied:
• Specific business reason provided
• Alternative dates suggested
• Partial approval options offered
• Split-time solutions considered

Appeal Process
If you disagree with a denial:
1. Discuss concerns with direct supervisor
2. Escalate to department manager if needed
3. Contact HR for policy interpretation
4. Document all communications

SECTION 5: BEST PRACTICES FOR PTO PLANNING

Annual Planning Strategy
• Schedule major vacations in January
• Consider family schedules and school calendars
• Plan around known business cycles
• Book popular holiday periods early
• Spread usage throughout the year

Quarterly Planning Sessions
Q1 Planning (January-March)
• Submit summer vacation requests
• Plan spring break coverage
• Consider tax season impacts

Q2 Planning (April-June)
• Finalize summer plans
• Plan fall vacation requests
• Review year-to-date usage

Q3 Planning (July-September)
• Submit holiday period requests
• Plan year-end usage strategy
• Consider carryover implications

Q4 Planning (October-December)
• Use remaining balance
• Plan following year requests
• Complete annual usage goals

Team Coordination Best Practices
• Share vacation calendars with team
• Coordinate major time off periods
• Cross-train on essential functions
• Develop coverage rotation schedules
• Communicate well in advance

SECTION 6: YEAR-END MANAGEMENT

Carryover Rules and Deadlines
• Maximum carryover: 40 hours (5 days)
• Carryover deadline: December 31
• No exceptions or extensions granted
• No cash payout for forfeited time

Use-It-or-Lose-It Strategy
September: Balance Assessment
• Review current PTO balance
• Calculate year-end usage needs
• Identify available request periods
• Submit November/December requests

October: Final Planning
• Confirm holiday period availability
• Consider partial day usage options
• Plan around business restrictions
• Submit remaining requests

November: Last Chance Usage
• Take advantage of partial day options
• Use PTO for medical appointments
• Consider mental health days
• Complete documentation for carryover

December Blackout Considerations
Department-specific restrictions:
• Retail Operations: December 15-31
• Accounting/Finance: December 20-January 5
• Customer Service: December 23-26
• IT Systems: December 28-January 2
• Shipping/Receiving: December 20-30

SECTION 7: INTEGRATION WITH OTHER LEAVE TYPES

PTO and Sick Leave Coordination
• Separate benefit pools - cannot combine
• Sick leave for health-related absences only
• PTO for planned personal time off
• Different approval processes apply

Holiday Integration
• Company holidays do not deduct from PTO
• "Sandwich days" between holidays popular
• Floating holidays managed separately
• Holiday closure days protected

FMLA and Disability Coordination
• PTO may be required for FMLA leave
• Coordinate with Benefits team early
• Medical documentation requirements
• Job protection rules apply
• Return-to-work procedures

SECTION 8: TECHNOLOGY RESOURCES AND SUPPORT

Employee Portal Features
• Real-time balance tracking and updates
• Request submission and approval workflow
• Calendar integration with Outlook/Google
• Mobile app for on-the-go access
• Historical usage reports and analytics

Mobile Application
Download "Akme Employee" app:
• iOS: Available in App Store
• Android: Available in Google Play
• Features: Request submission, balance checking, approval notifications

System Integration
• Payroll system automatic deduction
• Calendar sync with company systems
• Time clock integration for hourly employees
• Manager dashboard for team oversight

Troubleshooting Common Issues
• Password reset: Use "Forgot Password" link
• Balance discrepancies: Contact payroll
• System errors: Submit IT support ticket
• Mobile app issues: Update to latest version

SECTION 9: FREQUENTLY ASKED QUESTIONS

Q: Can I request PTO during my first 90 days?
A: No, PTO benefits begin after 90 days of employment. Emergency situations may be considered on case-by-case basis.

Q: What happens to my PTO if I leave the company?
A: No cash payout is provided for unused PTO upon termination.

Q: Can I donate PTO to a coworker in need?
A: Currently, PTO donation programs are not available.

Q: How do I change a submitted request?
A: Cancel the original request and submit a new one if not yet approved.

Q: Can managers require employees to use PTO?
A: Generally no, except in cases of excessive accrual near caps.

SECTION 10: CONTACT INFORMATION AND SUPPORT

Human Resources Support
• General PTO Questions: hr-pto@akme.com
• Policy Interpretation: hr-policy@akme.com
• System Technical Issues: portal-support@akme.com
• Urgent After-Hours: (555) 123-4567 ext. 9999

Self-Service Resources
• Employee Portal: portal.akme.com/timeoff
• PTO Calculator: Available in portal tools
• Video Tutorials: Training section of portal
• FAQ Database: Knowledge base with search

Manager Resources
• Manager Toolkit: portal.akme.com/managers
• Approval Guidelines: Downloadable PDF
• Training Schedule: HR training calendar
• Policy Updates: Quarterly manager briefings

Document Certification
This guide is certified current as of January 1, 2024.
Next scheduled review: December 31, 2024.
All information subject to official policy documents.`
        };
        
      case 'benefits guide 2024':
        return {
          title: 'Employee Benefits Comprehensive Guide 2024',
          version: '4.1',
          lastUpdated: '2023-11-15',
          author: 'Benefits Administration Team',
          department: 'Human Resources - Benefits',
          classification: 'Confidential',
          accessLevel: 'All Employees',
          documentType: 'Guide',
          approvalStatus: 'Approved',
          nextReview: '2024-10-01',
          content: `AKME CORPORATION
EMPLOYEE BENEFITS COMPREHENSIVE GUIDE 2024

Document ID: HR-BEN-GUIDE-2024
Version: 4.1
Classification: Confidential - Employee Distribution Only
Effective Date: January 1, 2024

EXECUTIVE SUMMARY

Welcome to your comprehensive benefits guide for 2024. This document outlines all employee benefits available through Akme Corporation, including eligibility requirements, coverage details, and enrollment procedures.

Important Notice: This guide contains confidential benefit information and premium rates that are proprietary to Akme Corporation and its employees.

OPEN ENROLLMENT INFORMATION

2024 Open Enrollment Schedule
• Enrollment Period: October 15 - November 5, 2024
• Benefits Effective Date: January 1, 2025
• Required Action: All employees must make active elections
• Late Enrollment: Not permitted except qualifying life events

What's New for 2024
• Enhanced mental health coverage at 100% preventive level
• Increased HSA company contributions
• New wellness incentive programs
• Expanded telemedicine options
• Added fertility and family planning benefits

Enrollment Methods
1. Online Benefits Portal: benefits.akme.com
2. Phone Support: (555) 123-4567
3. In-Person Benefits Fair: October 20-22, 2024
4. One-on-One Consultations: Schedule via portal

MEDICAL INSURANCE PLANS

Plan Option 1: PPO Select Plan

Monthly Premium Contributions (Employee Portion)
Coverage Tier                    2024 Premium
Employee Only                    $125.00
Employee + Spouse               $310.00
Employee + Child(ren)           $245.00
Family Coverage                 $420.00

Plan Design Details
Annual Deductible              Individual: $1,000 | Family: $2,000
Out-of-Pocket Maximum          Individual: $5,000 | Family: $10,000
Primary Care Physician         $25 copay after deductible
Specialist Visits              $45 copay after deductible
Emergency Room                 $150 copay (waived if admitted)
Urgent Care                    $35 copay
Inpatient Hospital            10% coinsurance after deductible
Outpatient Surgery            15% coinsurance after deductible

Prescription Drug Coverage
Generic Drugs                  $10 copay
Preferred Brand                $30 copay
Non-Preferred Brand            $60 copay
Specialty Medications          20% coinsurance (max $150/month)

Network Information
Provider Network: Blue Cross Blue Shield PPO
National Coverage: 95% provider acceptance rate
Out-of-Network: Available with higher costs

Plan Option 2: HMO Advantage Plan

Monthly Premium Contributions (Employee Portion)
Coverage Tier                    2024 Premium
Employee Only                    $85.00
Employee + Spouse               $245.00
Employee + Child(ren)           $185.00
Family Coverage                 $315.00

Plan Design Details
Annual Deductible              Individual: $500 | Family: $1,000
Out-of-Pocket Maximum          Individual: $3,500 | Family: $7,000
Primary Care Physician         $20 copay (no deductible)
Specialist Visits              $35 copay (referral required)
Emergency Room                 $100 copay
Urgent Care                    $25 copay
Inpatient Hospital            $250 copay per admission
Outpatient Surgery            $150 copay

Prescription Drug Coverage
Generic Drugs                  $8 copay
Preferred Brand                $25 copay
Non-Preferred Brand            $50 copay
Specialty Medications          15% coinsurance (max $100/month)

Network Information
Provider Network: Regional HMO Network
Coverage Area: Metropolitan area and surrounding counties
Coordinated Care: PCP referrals required for specialists

Plan Option 3: High-Deductible Health Plan (HDHP) with HSA

Monthly Premium Contributions (Employee Portion)
Coverage Tier                    2024 Premium
Employee Only                    $45.00
Employee + Spouse               $145.00
Employee + Child(ren)           $115.00
Family Coverage                 $210.00

Plan Design Details
Annual Deductible              Individual: $2,500 | Family: $5,000
Out-of-Pocket Maximum          Individual: $6,000 | Family: $12,000
Preventive Care               100% covered (no deductible)
All Other Services            Subject to deductible, then 80/20 coinsurance
Emergency Room                Deductible + 20% coinsurance
Prescription Drugs            Deductible + 20% coinsurance

Health Savings Account (HSA) Benefits
Company Annual Contribution    Individual: $750 | Family: $1,500
Employee Contribution Limit   Individual: $4,150 | Family: $8,300
Catch-Up Contribution (55+)   Additional $1,000
Tax Benefits                  Triple tax advantage

DENTAL INSURANCE

Comprehensive Dental Plan

Monthly Premium Contribution: $28.00 (employee only)
Family coverage available with additional premium

Coverage Details
Annual Deductible             $50 per person ($150 family maximum)
Annual Benefit Maximum        $2,500 per person

Service Categories
Preventive Care (Class I)     100% coverage (no deductible)
• Routine cleanings, exams, X-rays
• Fluoride treatments, sealants

Basic Procedures (Class II)   80% coverage (after deductible)
• Fillings, simple extractions
• Periodontal maintenance

Major Procedures (Class III)  50% coverage (after deductible)
• Crowns, bridges, dentures
• Root canals, oral surgery

Orthodontic Coverage         50% coverage
• Lifetime maximum: $2,000 per person
• Available for children and adults

Network Information
Provider: Delta Dental PPO
Network Size: 95,000+ dentists nationwide
Online Provider Directory: deltadentalppmo.com

VISION INSURANCE

Vision Care Plan

Monthly Premium Contribution: $12.00 (employee only)
Family coverage available with additional premium

Coverage Benefits
Comprehensive Eye Exams      $10 copay (annual)
Routine Eye Exams           100% covered annually

Eyewear Benefits
Prescription Glasses        $200 frame allowance (every 24 months)
Standard Lenses            100% covered
Progressive Lenses         $50 copay
Contact Lenses             $200 allowance annually (in lieu of glasses)

Additional Services
Laser Vision Correction    15% discount at participating providers
Safety Glasses            Additional $100 allowance for job-required eyewear

Network Information
Provider: VSP Choice Network
Network Size: 39,000+ providers nationwide
Online Provider Directory: vsp.com

RETIREMENT BENEFITS

401(k) Retirement Plan

Company Matching
Matching Formula: 100% of first 6% employee contributions
Vesting Schedule: Immediate 100% vesting
Maximum Annual Contribution: $23,000 (2024 IRS limit)
Catch-Up Contributions: Additional $7,500 if age 50+ by year-end

Eligibility
Employment Requirement: First of month following 30 days
Auto-Enrollment: 3% contribution rate for new hires
Opt-Out Period: 90 days from hire date

Investment Options
Target Date Funds: Age-appropriate asset allocation (5 options)
Equity Funds: Domestic and international options (8 funds)
Fixed Income Funds: Bond and stable value options (4 funds)
Company Stock: Limited to 10% of total balance

Plan Features
Loan Provisions: Up to $50,000 or 50% of balance
Investment Advisory: Professional guidance available
Online Tools: Retirement planning calculators
Quarterly Statements: Mailed and online access

Roth 401(k) Option
After-Tax Contributions: Same limits as traditional 401(k)
Company Matching: Applied to traditional 401(k) portion
Tax Treatment: Tax-free growth and qualified withdrawals
Conversion Options: In-service Roth conversions available

HEALTH SAVINGS ACCOUNT (HSA)

Eligibility Requirements
Available only to High-Deductible Health Plan participants
No other health coverage permitted
Cannot be claimed as dependent on another person's tax return

2024 Contribution Limits
Individual Coverage: $4,150 maximum
Family Coverage: $8,300 maximum
Age 55+ Catch-Up: Additional $1,000

Company Contributions
Individual HDHP: $750 contributed annually
Family HDHP: $1,500 contributed annually
Contribution Schedule: Quarterly deposits

Account Features
Investment Options: Available when balance exceeds $2,000
Rollover Benefit: Funds never expire
Portability: Account remains yours if you leave
Debit Card: Convenient access to funds

Qualified Expenses
Medical, dental, and vision costs not covered by insurance
Over-the-counter medications and supplies
Long-term care premiums and services
Medicare premiums (after age 65)

FLEXIBLE SPENDING ACCOUNTS

Healthcare FSA
2024 Contribution Limit: $3,200
Grace Period: 2.5 months to use remaining funds
Eligible Expenses: Medical, dental, vision deductibles and copays

Dependent Care FSA
2024 Contribution Limit: $5,000 (married filing jointly)
Eligible Expenses: Daycare, preschool, after-school programs
Age Limitations: Children under 13 or disabled dependents

LIFE AND DISABILITY INSURANCE

Basic Life Insurance (Company-Paid)
Coverage Amount: 1x annual salary
Maximum Coverage: $200,000
Beneficiary Designation: Required
Portability: Conversion option upon termination

Voluntary Life Insurance
Employee Coverage: 1x to 5x salary (max $500,000)
Spouse Coverage: $10,000 to $100,000 (max 50% of employee)
Child Coverage: $5,000 or $10,000 per child
Age-Based Premiums: Starting at $0.10 per $1,000 monthly

Short-Term Disability (Company-Paid)
Benefit Amount: 60% of pre-disability earnings
Maximum Weekly Benefit: $1,500
Elimination Period: 14 calendar days
Benefit Duration: Up to 24 weeks

Long-Term Disability (Company-Paid)
Benefit Amount: 60% of pre-disability earnings
Maximum Monthly Benefit: $8,000
Elimination Period: 180 calendar days
Benefit Duration: To Social Security Normal Retirement Age

ADDITIONAL BENEFITS AND PROGRAMS

Employee Assistance Program (EAP)
Counseling Services: 8 free sessions per issue per year
Work-Life Resources: Financial and legal consultation
24/7 Access: Phone, online, and mobile app support
Family Coverage: Services available to household members

Wellness Programs
Annual Health Screening: Up to $500 premium discount
Fitness Reimbursement: 50% up to $300 annually
Mental Health Enhancement: Therapy visits at 100% preventive level
Smoking Cessation: Free programs and nicotine replacement

Commuter Benefits
Transit Benefits: Up to $315 per month pre-tax
Parking Benefits: Pre-tax payroll deduction available
Bicycle Commuter: $20 monthly reimbursement

Professional Development
Tuition Reimbursement: Up to $3,000 annually
Professional Certifications: Exam fees and study materials
Conference Attendance: Subject to budget and approval
LinkedIn Learning: Company-wide access

QUALIFYING LIFE EVENTS

Events Allowing Mid-Year Changes
• Marriage or divorce
• Birth, adoption, or placement for adoption
• Death of spouse or dependent
• Change in spouse's employment affecting benefits
• Significant change in coverage or cost
• Change in residence affecting plan availability

Required Documentation and Timeline
Event Documentation Required: Marriage certificate, birth certificate, etc.
Notification Deadline: 31 days from qualifying event
Coverage Effective Date: Generally first of month following event
Documentation Deadline: 60 days to provide required documents

CONTACT INFORMATION AND RESOURCES

Benefits Administration Team
Phone: (555) 123-4567
Email: benefits@akme.com
Office Hours: Monday-Friday, 8:00 AM - 5:00 PM EST

Online Resources
Benefits Portal: benefits.akme.com
Provider Directories: Links available through portal
Educational Materials: Videos, guides, and calculators

Insurance Carrier Contacts
Medical Plans: Blue Cross Blue Shield (800) 555-0123
Dental Plan: Delta Dental (800) 555-0124
Vision Plan: VSP (800) 555-0125
Life/Disability: MetLife (800) 555-0126

IMPORTANT LEGAL NOTICES

This benefits guide provides a summary of your benefits. The official plan documents, summary plan descriptions, and insurance contracts contain the complete terms and conditions of coverage and govern in case of any discrepancy with this guide.

Akme Corporation reserves the right to modify, suspend, or terminate any benefit plan at any time, subject to applicable law and existing contracts.

CONFIDENTIALITY NOTICE

This document contains confidential and proprietary information belonging to Akme Corporation. Distribution is restricted to eligible employees and their immediate family members for benefits decision-making purposes only.

Unauthorized disclosure of premium rates, plan designs, or other benefit information to competitors or external parties is strictly prohibited and may result in disciplinary action.

Document Security: This guide should be stored securely and not shared outside your household.`
        };
        
      default:
        return {
          title: documentTitle,
          version: '1.0',
          lastUpdated: '2024-01-01',
          author: 'HR Department',
          department: 'Human Resources',
          classification: 'Internal',
          accessLevel: 'All Employees',
          documentType: 'Policy',
          approvalStatus: 'Under Review',
          nextReview: '2024-12-31',
          content: `AKME CORPORATION
EMPLOYEE POLICY DOCUMENT

Document Title: ${documentTitle}
Document ID: TBD
Version: 1.0
Status: Under Development

DOCUMENT STATUS NOTICE

This document is currently being developed and reviewed by the appropriate teams. 

OVERVIEW

This document will contain important information about Akme Corporation policies and procedures that apply to all employees.

TEMPORARY INFORMATION

While this document is being finalized, please contact Human Resources for any immediate questions or policy clarifications:

Human Resources Department
Phone: (555) 123-4567
Email: hr@akme.com
Employee Portal: portal.akme.com

NEXT STEPS

1. Document development in progress
2. Legal review and approval pending
3. Final formatting and distribution planned
4. Employee communication and training scheduled

This placeholder will be replaced with complete policy information once the document review process is completed.

Thank you for your patience while we ensure all policy information is accurate and current.`
        };
    }
  };

  const document = getDocumentContent(documentTitle);

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'Public': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Internal': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Confidential': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Restricted': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getClassificationIcon = (classification: string) => {
    switch (classification) {
      case 'Public': return Eye;
      case 'Internal': return Building;
      case 'Confidential': return Shield;
      case 'Restricted': return Lock;
      default: return FileText;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Under Review': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'Draft': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'Archived': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const ClassificationIcon = getClassificationIcon(document.classification);

  const adjustZoom = (direction: 'in' | 'out') => {
    setZoomLevel(prev => {
      if (direction === 'in' && prev < 150) return prev + 10;
      if (direction === 'out' && prev > 75) return prev - 10;
      return prev;
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-5xl p-0 flex flex-col">
        {/* Header Section */}
        <div className="flex-shrink-0 border-b bg-white dark:bg-gray-900">
          <div className="p-6 pb-4">
            <SheetHeader className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    <FileText className="h-7 w-7 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="space-y-2">
                    <SheetTitle className="text-xl leading-7 pr-8">
                      {document.title}
                    </SheetTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`${getClassificationColor(document.classification)} font-medium`}>
                        <ClassificationIcon className="h-3 w-3 mr-1.5" />
                        {document.classification}
                      </Badge>
                      <Badge variant="outline" className="bg-slate-50 dark:bg-slate-800">
                        <User className="h-3 w-3 mr-1.5" />
                        {document.accessLevel}
                      </Badge>
                      <Badge className={`${getStatusColor(document.approvalStatus)} font-medium`}>
                        <CheckCircle className="h-3 w-3 mr-1.5" />
                        {document.approvalStatus}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              
              <SheetDescription className="text-sm text-muted-foreground">
                {document.documentType} • Version {document.version} • {document.department}
              </SheetDescription>
            </SheetHeader>

            {/* Document Metadata Grid */}
            <div className="mt-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                    <User className="h-3 w-3" />
                    Author
                  </div>
                  <div className="font-medium text-sm">{document.author}</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                    <Clock className="h-3 w-3" />
                    Last Updated
                  </div>
                  <div className="font-medium text-sm">{document.lastUpdated}</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                    <Calendar className="h-3 w-3" />
                    Next Review
                  </div>
                  <div className="font-medium text-sm">{document.nextReview}</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                    <Shield className="h-3 w-3" />
                    Security Level
                  </div>
                  <div className="font-medium text-sm">{document.classification}</div>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            {(document.classification === 'Confidential' || document.classification === 'Restricted') && (
              <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-800 dark:text-amber-200 text-sm">Security Notice</div>
                    <div className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                      This document contains {document.classification.toLowerCase()} information. 
                      Do not share outside authorized personnel. Access is logged and monitored.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button variant="outline" size="sm">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Link
                </Button>
                <Button variant="outline" size="sm">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => adjustZoom('out')}
                  disabled={zoomLevel <= 75}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium px-2 min-w-[4rem] text-center">
                  {zoomLevel}%
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => adjustZoom('in')}
                  disabled={zoomLevel >= 150}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Document Content Area */}
        <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-8">
              {/* Document Paper Background */}
              <div 
                className="bg-white dark:bg-gray-800 shadow-lg rounded-lg mx-auto max-w-4xl"
                style={{ 
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: 'top center',
                  width: zoomLevel !== 100 ? `${10000 / zoomLevel}%` : '100%'
                }}
              >
                <div className="p-12">
                  {/* Document Content */}
                  <div className="prose prose-gray dark:prose-invert max-w-none">
                    {document.content.split('\n').map((line, index) => {
                      const trimmedLine = line.trim();
                      
                      if (!trimmedLine) {
                        return <div key={index} className="h-4" />;
                      }
                      
                      // Company header styling
                      if (trimmedLine === 'AKME CORPORATION' || trimmedLine.includes('AKME CORPORATION')) {
                        return (
                          <div key={index} className="text-center mb-6 pb-4 border-b-2 border-blue-600">
                            <h1 className="text-3xl font-bold text-blue-900 dark:text-blue-100 tracking-wide">
                              {trimmedLine}
                            </h1>
                          </div>
                        );
                      }
                      
                      // Main document title
                      if (index > 0 && index < 10 && trimmedLine.length > 20 && !trimmedLine.startsWith('Document')) {
                        return (
                          <div key={index} className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {trimmedLine}
                            </h2>
                          </div>
                        );
                      }
                      
                      // Document metadata
                      if (trimmedLine.startsWith('Document ID:') || 
                          trimmedLine.startsWith('Version:') || 
                          trimmedLine.startsWith('Effective Date:') ||
                          trimmedLine.startsWith('Classification:')) {
                        return (
                          <p key={index} className="text-sm text-gray-600 dark:text-gray-400 mb-1 font-medium">
                            {trimmedLine}
                          </p>
                        );
                      }
                      
                      // Section headers (ALL CAPS)
                      if (trimmedLine === trimmedLine.toUpperCase() && 
                          trimmedLine.length > 5 && 
                          !trimmedLine.includes(':') &&
                          !trimmedLine.startsWith('•')) {
                        return (
                          <h3 key={index} className="text-lg font-bold text-blue-800 dark:text-blue-200 mt-8 mb-4 pb-2 border-b border-gray-300 dark:border-gray-600">
                            {trimmedLine}
                          </h3>
                        );
                      }
                      
                      // Subsection headers
                      if (trimmedLine.match(/^[A-Za-z].*[A-Za-z\)]$/) && 
                          !trimmedLine.includes('•') && 
                          !trimmedLine.includes(':') && 
                          trimmedLine.length > 10 && 
                          trimmedLine.length < 80) {
                        return (
                          <h4 key={index} className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-3">
                            {trimmedLine}
                          </h4>
                        );
                      }
                      
                      // Bullet points
                      if (trimmedLine.startsWith('•')) {
                        return (
                          <li key={index} className="ml-6 mb-2 text-gray-700 dark:text-gray-300 list-none relative">
                            <span className="absolute -left-4 text-blue-600">•</span>
                            {trimmedLine.substring(1).trim()}
                          </li>
                        );
                      }
                      
                      // Table-like data (contains multiple spaces or tabs)
                      if (trimmedLine.includes('    ') || trimmedLine.includes('\t')) {
                        return (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded my-2 font-mono text-sm">
                            <pre className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                              {trimmedLine}
                            </pre>
                          </div>
                        );
                      }
                      
                      // Regular paragraphs
                      return (
                        <p key={index} className="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                          {trimmedLine}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              <span>Use Ctrl+F to search within this document</span>
            </div>
            <div className="text-xs">
              Document ID: {document.classification.toUpperCase()}-{document.documentType.toUpperCase()}-{new Date().getFullYear()}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}